/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useBreakpoint, useMixpanel } from 'hooks';
import styled from 'styled-components';
import { Tooltip as AntdTooltip, RadioGroup, RadioButton } from 'components';
import { RadioChangeEvent } from 'antd';
import { featureEnabled, MAX_NOTICE_IN_MINUTES } from 'utils';
import { MixpanelPage } from 'hooks/useMixpanel/events';
import { HashRouter, Route } from 'react-router-dom';
import { AuthContextUser } from 'contexts';
import { ApproveShowingOption, approveShowingsOptions } from '../../constants';
import Toggle from '../../react-common/Toggle';

// 15 min increments up to 4 hours
export type MaxShowingDurationValues =
  | 15
  | 30
  | 45
  | 60
  | 75
  | 90
  | 105
  | 120
  | 135
  | 150
  | 165
  | 180
  | 195
  | 210
  | 225
  | 240;

export type ShowingSettingsProps = {
  notice: number;
  noticeMinutes: number;
  overnightNotice: boolean;
  maxDuration: MaxShowingDurationValues;
  doubleBookings: boolean;
  notifyOverlappingAppt: boolean;
  advAutoConfEnabled: boolean;
  maxOverlap: number;
  bufferSlots: number;
  showingInstructions?: string;
  showVendorInstructions?: boolean;
  vendorInstructions?: string;
  approveShowingOptions: {
    contactsToConfirm: boolean;
    autoConfirm: boolean;
    listingAgentConfirm: {
      enabled: boolean;
      excludeFromQueue: boolean;
    };
  };
  scrollToAptRestrictions: () => void;
  setAgentOnlyConfirm: () => void;
  setShowingSettings: (showingSettings: Partial<ShowingSettingsProps>) => void;
  noMoreShowings: boolean;
  setIsMustCallDisabled: (val: boolean) => void;
  auth: AuthContextUser;
};

interface MaxShowingDurationOption {
  label: string;
  value: number;
}

const maxShowingDurationOptions: Array<MaxShowingDurationOption> = [
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '45m', value: 45 },
  { label: '1hr', value: 60 },
  { label: 'Custom', value: 0 },
];

const maxShowingDurationCustomHourOptions: Array<MaxShowingDurationOption> = [
  { label: '1hr', value: 60 },
  { label: '2hr', value: 120 },
  { label: '3hr', value: 180 },
  { label: '4hr', value: 240 },
];

const maxShowingDurationCustomMinOptions: Array<MaxShowingDurationOption> = [
  { label: '0m', value: 0 },
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '45m', value: 45 },
];

const overlapOptions = [
  { label: 'No Limit', value: -1 },
  ...Array(4)
    .fill(0)
    .map((_, i) => ({
      label: `Max ${i + 2} overlapping showings`,
      value: i + 2,
    })),
];

const bufferOptions = [
  { label: 'No buffer between showings', value: 0 },
  { label: '15 min buffer between showings', value: 1 },
  { label: '30 min buffer between showings', value: 2 },
  { label: '1 hour buffer between showings', value: 4 },
  { label: '2 hour buffer between showings', value: 8 },
];

const swalAutoConfirmOptions: SweetAlert.Settings &
  SweetAlert.AlertModalSettings = {
  title: 'Enable AUTO-CONFIRM',
  text: 'By enabling AUTO-CONFIRM, confirmation instructions may be automatically sent to the showing agent upon booking.\nThis also disables LA to confirm.\nAre you sure?',
  html: true,
  type: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Enable',
  closeOnConfirm: true,
  closeOnCancel: true,
};

const ShowingSettings = ({
  advAutoConfEnabled,
  notice,
  noticeMinutes,
  overnightNotice,
  maxDuration,
  doubleBookings,
  maxOverlap,
  bufferSlots,
  notifyOverlappingAppt,
  showingInstructions,
  vendorInstructions,
  approveShowingOptions: { autoConfirm, listingAgentConfirm },
  scrollToAptRestrictions,
  setAgentOnlyConfirm,
  setShowingSettings: _setShowingSettings,
  noMoreShowings,
  setIsMustCallDisabled,
  auth,
}: ShowingSettingsProps) => {
  const [showingSettings, setShowingSettings] = useState<
    Partial<ShowingSettingsProps>
  >({
    notice,
    noticeMinutes,
    overnightNotice,
    maxDuration,
    doubleBookings,
    maxOverlap,
    bufferSlots,
    notifyOverlappingAppt,
    showingInstructions,
    showVendorInstructions: !!vendorInstructions,
    vendorInstructions,
    approveShowingOptions: {
      contactsToConfirm:
        advAutoConfEnabled || (!autoConfirm && !listingAgentConfirm?.enabled),
      autoConfirm: !advAutoConfEnabled && autoConfirm,
      listingAgentConfirm: {
        enabled: !advAutoConfEnabled && listingAgentConfirm?.enabled,
        excludeFromQueue: listingAgentConfirm?.excludeFromQueue,
      },
    },
  });

  useEffect(() => {
    _setShowingSettings(showingSettings);
  }, [_setShowingSettings, showingSettings]);

  const { xl } = useBreakpoint();

  useMixpanel({
    pageTracking: { pageName: MixpanelPage.ShowingConfiguration },
    auth,
  });

  // when advanced auto confirm is enabled, 'Seller / Contacts' must be selected
  useEffect(() => {
    setShowingSettings(prev => ({
      ...prev,
      approveShowingOptions: {
        contactsToConfirm:
          advAutoConfEnabled ||
          (!prev.approveShowingOptions.autoConfirm &&
            !prev.approveShowingOptions.listingAgentConfirm?.enabled),
        autoConfirm:
          !advAutoConfEnabled && prev.approveShowingOptions.autoConfirm,
        listingAgentConfirm: {
          enabled:
            !advAutoConfEnabled &&
            prev.approveShowingOptions.listingAgentConfirm?.enabled,
          excludeFromQueue:
            prev.approveShowingOptions.listingAgentConfirm?.excludeFromQueue,
        },
      },
    }));
  }, [advAutoConfEnabled]);

  useEffect(() => {
    setIsMustCallDisabled(
      showingSettings.approveShowingOptions?.autoConfirm || false,
    );
  }, [
    setIsMustCallDisabled,
    showingSettings.approveShowingOptions?.autoConfirm,
  ]);

  const updateApproveShowingsOptions = (
    selection: ApproveShowingOption['value'],
  ) => {
    if (selection !== 'contactsToConfirm') setAgentOnlyConfirm();

    setShowingSettings(prev => ({
      ...prev,
      approveShowingOptions: {
        contactsToConfirm: false,
        autoConfirm: false,
        [selection]: true,
        listingAgentConfirm: {
          enabled: selection === 'listingAgentConfirm',
          excludeFromQueue: false,
        },
      },
    }));
  };

  useEffect(() => {
    if (noMoreShowings && showingSettings.approveShowingOptions.autoConfirm) {
      updateApproveShowingsOptions('contactsToConfirm');
    }
  }, [noMoreShowings]);

  const minNoticeErrorText = useMemo(() => {
    const numericNotice = +(showingSettings.notice ?? 0);
    const numericNoticeMinutes = +(showingSettings.noticeMinutes ?? 0);

    if (featureEnabled('minNoticeFeature')) {
      const totalNoticeInMinutes = numericNotice * 60 + numericNoticeMinutes;

      return totalNoticeInMinutes >= MAX_NOTICE_IN_MINUTES ||
        numericNotice < 0 ||
        numericNoticeMinutes > 59
        ? 'Minimum notice must be between 0-99 hours and 0-59 minutes.'
        : '';
    }
    return numericNotice > 100 || numericNotice < 0
      ? 'Minimum notice must be between 0 and 100 hours.'
      : '';
  }, [showingSettings.notice, showingSettings.noticeMinutes]);

  const handleApproveShowingsOptionsChange = (
    selection: ApproveShowingOption['value'],
  ) =>
    selection === 'autoConfirm'
      ? swal(
          swalAutoConfirmOptions,
          (confirmed: boolean) =>
            confirmed && updateApproveShowingsOptions(selection),
        )
      : updateApproveShowingsOptions(selection);

  // Max showing duration functions
  const [displayCustomMaxShowingDuration, setDisplayCustomMaxShowingDuration] =
    useState(showingSettings.maxDuration > 60);

  const [noticeHours, setNoticeHours] = useState<number | undefined | null>(
    showingSettings.notice,
  );
  const [noticeMins, setNoticeMins] = useState<number | undefined | null>(
    showingSettings.noticeMinutes,
  );

  useEffect(() => {
    if (notice == null && noticeMinutes == null) {
      // Set default notice to 1 hour
      setNoticeHours(1);
      setShowingSettings(prev => ({ ...prev, notice: 1 }));
    }
  }, []);

  const getMaxShowingDurationOption = (duration: number) => {
    if (duration <= 60) return duration;
    // Custom duration
    return maxShowingDurationOptions[4].value;
  };

  // Handle max showing duration radio button change
  const maxShowingDurationChangeHandler = (e: RadioChangeEvent) => {
    // Custom selected
    if (e.target.value === 0) {
      setDisplayCustomMaxShowingDuration(true);
      if (showingSettings.maxDuration <= 60) {
        setShowingSettings(prev => ({
          ...prev,
          maxDuration: 75,
        }));
      }
    }
    // One of the preset options selected
    else {
      setDisplayCustomMaxShowingDuration(false);
      setShowingSettings(prev => ({
        ...prev,
        maxDuration: e.target.value,
      }));
    }
  };

  // Set max showing duration on custom hour change
  const customMaxShowingDurationHourChange = (hours: number) => {
    // Hours are calculated in minutes (e.g. 1 hour = 60)
    const minutes = hours === 240 ? 0 : showingSettings.maxDuration % 60;
    const newMaxDuration = (hours + minutes) as MaxShowingDurationValues;
    setShowingSettings(prev => ({
      ...prev,
      maxDuration: newMaxDuration > 240 ? 240 : newMaxDuration,
    }));
  };

  // Set max showing duration on custom minutes change
  const customMaxShowingDurationMinChange = (minutes: number) => {
    // Hours are calculated in minutes (e.g. 1 hour = 60)
    const hours =
      showingSettings.maxDuration - (showingSettings.maxDuration % 60);
    const newMaxDuration = (hours + minutes) as MaxShowingDurationValues;
    setShowingSettings(prev => ({
      ...prev,
      maxDuration: newMaxDuration > 240 ? 240 : newMaxDuration,
    }));
  };

  return (
    <div className="form-group col-md-7 col-lg-6 m-t-md p-l">
      <legend className="p-b-xs">Showing Settings</legend>
      <div className="row">
        <div className="form-group col-sm-6 p-x-lg">
          <label className="m-b">Min. Showing Notice</label>
          <StyledContainer $xl={xl}>
            <label className="m-b display-flex align-items-center font-normal">
              <input
                name="notice"
                type="radio"
                style={{ marginRight: '15px', marginTop: 0 }}
                data-cy="overnight-notice-hours"
                checked={!showingSettings.overnightNotice}
                onChange={() => {
                  setShowingSettings(prev => ({
                    ...prev,
                    overnightNotice: false,
                  }));
                  setNoticeHours(showingSettings.notice ?? 1);
                }}
              />
              <input
                type="number"
                className="form-control m-r-sm"
                style={{ width: '70px' }}
                min="0"
                max="99"
                value={noticeHours ?? ''}
                disabled={showingSettings.overnightNotice}
                data-cy="overnight-notice-number-hours"
                onChange={e => {
                  const _notice = parseInt(e.target.value, 10);
                  if (!Number.isNaN(_notice)) {
                    setShowingSettings(prev => ({
                      ...prev,
                      notice: _notice,
                    }));
                    setNoticeHours(_notice);
                    return;
                  }
                  setNoticeHours(undefined);
                  setShowingSettings(prev => ({
                    ...prev,
                    notice: undefined,
                  }));
                }}
              />
              (Hours)
            </label>
            {featureEnabled('minNoticeFeature') && (
              <label className="m-b display-flex align-items-center font-normal">
                <input
                  type="number"
                  className="form-control m-r-sm m-l-sm"
                  style={{ width: '70px' }}
                  min="0"
                  max="59"
                  value={noticeMins ?? ''}
                  disabled={showingSettings.overnightNotice}
                  data-cy="overnight-notice-number-minutes"
                  onChange={e => {
                    const _noticeMinutes = parseInt(e.target.value, 10);
                    if (!Number.isNaN(_noticeMinutes)) {
                      setShowingSettings(prev => ({
                        ...prev,
                        noticeMinutes: _noticeMinutes,
                      }));
                      setNoticeMins(_noticeMinutes);
                      return;
                    }
                    setNoticeMins(undefined);
                    setShowingSettings(prev => ({
                      ...prev,
                      noticeMinutes: undefined,
                    }));
                  }}
                />
                (Minutes)
              </label>
            )}
          </StyledContainer>
          {minNoticeErrorText && (
            <label className="error" style={{ margin: '-12px 0 5px 0' }}>
              {minNoticeErrorText}
            </label>
          )}
          <label className="m-b display-flex align-items-center font-normal">
            <input
              name="notice"
              type="radio"
              style={{ marginRight: '15px', marginTop: 0 }}
              data-cy="overnight-notice-must-overnight"
              checked={showingSettings.overnightNotice}
              onChange={() => {
                setShowingSettings(prev => ({
                  ...prev,
                  overnightNotice: true,
                  notice: undefined,
                  noticeMinutes: undefined,
                }));
                setNoticeHours(undefined);
                setNoticeMins(undefined);
              }}
            />
            Overnight Notice Required{' '}
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id="tooltip-disable-users-time-slot">
                  Disables showings until the next available time slot the
                  following day.
                </Tooltip>
              }
            >
              <i className="fa fa-info-circle m-l-sm font-size-16"></i>
            </OverlayTrigger>
          </label>
          {/* Max Showing Duration Radio Buttons */}
          <div>
            <label className="m-t-md m-b">Max. Showing Duration</label>
            <RadioGroup
              value={getMaxShowingDurationOption(showingSettings.maxDuration)}
              size="small"
              onChange={maxShowingDurationChangeHandler}
            >
              {maxShowingDurationOptions.map(({ value, label }) => (
                <RadioButton
                  value={value}
                  key={value}
                  data-cy={`max-duration-${value}`}
                >
                  {label}
                </RadioButton>
              ))}
              ;
            </RadioGroup>
            <div className="display-flex">
              {/* Max Showing Duration Hours Dropdown */}
              {displayCustomMaxShowingDuration && (
                <div className="dropdown flex-child m-r-xs">
                  <button
                    className="btn btn-default btn-block dropdown-toggle"
                    style={{ textAlign: 'left' }}
                    type="button"
                    id="access-dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    data-toggle="dropdown"
                    data-cy="max-duration-hours"
                  >
                    {maxShowingDurationCustomHourOptions.find(
                      option =>
                        option.value ===
                        Math.floor(showingSettings.maxDuration / 60) * 60,
                    )?.label || ''}
                    <div
                      className="pull-right"
                      style={{ display: 'inline-block' }}
                    >
                      <span className="caret"></span>
                    </div>
                  </button>
                  <ul
                    className="dropdown-menu"
                    style={{ width: '100%' }}
                    aria-labelledby="access-dropdown"
                  >
                    {maxShowingDurationCustomHourOptions.map(
                      ({ value, label }) => (
                        <li
                          key={value}
                          onClick={() => {
                            customMaxShowingDurationHourChange(value);
                          }}
                        >
                          <a>{label}</a>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
              {/* Max Showing Duration Minutes Dropdown */}
              {displayCustomMaxShowingDuration && (
                <div className="dropdown flex-child">
                  <button
                    className="btn btn-default btn-block dropdown-toggle"
                    style={{ textAlign: 'left' }}
                    type="button"
                    id="access-dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    data-toggle="dropdown"
                    data-cy="max-duration-minutes"
                  >
                    {maxShowingDurationCustomMinOptions.find(
                      option =>
                        option.value === showingSettings.maxDuration % 60,
                    )?.label || ''}
                    <div
                      className="pull-right"
                      style={{ display: 'inline-block' }}
                    >
                      <span className="caret"></span>
                    </div>
                  </button>
                  <ul
                    className="dropdown-menu"
                    style={{ width: '100%' }}
                    aria-labelledby="access-dropdown"
                  >
                    {maxShowingDurationCustomMinOptions.map(
                      ({ value, label }) => (
                        <li
                          key={value}
                          onClick={() => {
                            customMaxShowingDurationMinChange(value);
                          }}
                        >
                          <a>{label}</a>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
          {/* Allow Overlapping Showings Toggle */}
          <div className="m-t-lg m-b display-flex justify-space-between align-items-start">
            <label style={{ fontSize: '12px' }}>
              Allow Overlapping Showings?
            </label>
            <Toggle
              onClick={() =>
                setShowingSettings(prev => ({
                  ...prev,
                  doubleBookings: !prev.doubleBookings,
                  notifyOverlappingAppt: true,
                  maxOverlap: -1,
                  bufferSlots: 0,
                }))
              }
              model={showingSettings.doubleBookings}
              dataCy="toggle-double-bookings"
            />
          </div>
          {showingSettings.doubleBookings ? (
            <>
              <div className="dropdown" style={{ marginTop: '10px' }}>
                <button
                  className="btn btn-default btn-block dropdown-toggle"
                  style={{ textAlign: 'left' }}
                  type="button"
                  id="access-dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  data-toggle="dropdown"
                  data-cy="max-overlap"
                >
                  {overlapOptions.find(
                    option => option.value === showingSettings.maxOverlap,
                  )?.label || overlapOptions[0].label}
                  <div
                    className="pull-right"
                    style={{ display: 'inline-block' }}
                  >
                    <span className="caret"></span>
                  </div>
                </button>
                <ul
                  className="dropdown-menu"
                  style={{ width: '100%' }}
                  aria-labelledby="access-dropdown"
                >
                  {overlapOptions.map(({ value, label }) => (
                    <li
                      key={value}
                      onClick={() => {
                        setShowingSettings(prev => ({
                          ...prev,
                          maxOverlap: value,
                        }));
                      }}
                    >
                      <a>{label}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <label
                className="display-flex align-items-start font-normal"
                style={{ marginTop: '10px' }}
              >
                <input
                  style={{ marginRight: '10px' }}
                  type="checkbox"
                  data-cy="notify-overlapping-appts"
                  checked={showingSettings.notifyOverlappingAppt}
                  onChange={() =>
                    setShowingSettings(prev => ({
                      ...prev,
                      notifyOverlappingAppt: !prev.notifyOverlappingAppt,
                    }))
                  }
                />
                <span>Notify agents regarding overlapping showings</span>
              </label>
            </>
          ) : (
            <div className="dropdown" style={{ marginTop: '10px' }}>
              <button
                className="btn btn-default btn-block dropdown-toggle"
                style={{ textAlign: 'left' }}
                type="button"
                id="access-dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                data-toggle="dropdown"
                data-cy="max-overlap"
              >
                {bufferOptions.find(
                  option => option.value === showingSettings.bufferSlots,
                )?.label || bufferOptions[0].label}
                <div className="pull-right" style={{ display: 'inline-block' }}>
                  <span className="caret"></span>
                </div>
              </button>
              <ul
                className="dropdown-menu"
                style={{ width: '100%' }}
                aria-labelledby="access-dropdown"
              >
                {bufferOptions.map(({ value, label }) => (
                  <li
                    key={value}
                    onClick={() => {
                      setShowingSettings(prev => ({
                        ...prev,
                        bufferSlots: value,
                      }));
                    }}
                  >
                    <a>{label}</a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="form-group col-sm-6 p-x-lg">
          <label className="m-b">Who should approve showings?</label>
          {advAutoConfEnabled && (
            <div className="m-b-none attention">
              <b>
                This listing has advanced auto-confirm settings configured
                below.
              </b>{' '}
              <span onClick={() => scrollToAptRestrictions()}>
                <i
                  className="fa fa-arrow-circle-down"
                  style={{ color: 'red' }}
                ></i>
              </span>
              <br />
              <br />
            </div>
          )}
          {/* Approve Showing Options */}
          {approveShowingsOptions.map(({ value, label, dataCy }) => (
            <AntdTooltip
              key={value}
              title={
                value === 'autoConfirm' &&
                noMoreShowings &&
                'Auto-confirm is disabled. To select this option, disable Turn Off Showings.'
              }
            >
              <div className="pretty p-icon p-round bb-pulse m-b message-content">
                <input
                  key={value}
                  name="approveShowings"
                  type="radio"
                  data-cy={`confirm-${dataCy}`}
                  value={value}
                  disabled={
                    (advAutoConfEnabled &&
                      ['autoConfirm', 'listingAgentConfirm'].includes(value)) ||
                    (value === 'autoConfirm' && noMoreShowings)
                  }
                  checked={
                    ['contactsToConfirm', 'autoConfirm'].includes(value)
                      ? showingSettings.approveShowingOptions[
                          value as 'contactsToConfirm' | 'autoConfirm'
                        ]
                      : showingSettings.approveShowingOptions
                          .listingAgentConfirm.enabled
                  }
                  onChange={e =>
                    handleApproveShowingsOptionsChange(
                      e.target.value as ApproveShowingOption['value'],
                    )
                  }
                />

                <div className="state p-success">
                  <i className="icon fa fa-check"></i>
                  <label>
                    {label}{' '}
                    {value === 'autoConfirm' && (
                      <i
                        className={`fa fa-bolt m-l-n${
                          showingSettings.approveShowingOptions.autoConfirm
                            ? ' glow'
                            : ''
                        }`}
                      ></i>
                    )}{' '}
                  </label>
                </div>
              </div>
            </AntdTooltip>
          ))}
          {showingSettings.approveShowingOptions.listingAgentConfirm
            ?.enabled && (
            <label className="display-flex m-l">
              <input
                type="checkbox"
                checked={
                  showingSettings.approveShowingOptions.listingAgentConfirm
                    .excludeFromQueue
                }
                onChange={e => {
                  const { checked } = e.target;
                  setShowingSettings(prev => ({
                    ...prev,
                    approveShowingOptions: {
                      ...prev.approveShowingOptions,
                      listingAgentConfirm: {
                        ...prev.approveShowingOptions.listingAgentConfirm,
                        excludeFromQueue: checked,
                      },
                    },
                  }));
                }}
                data-cy="hide-appt-from-queue"
              />
              <span className="m-l-sm font-normal">
                Hide Showings From Queue
              </span>
            </label>
          )}
          <div className="m-t-md m-b display-flex align-items-center">
            <label className="m-b-none attention">
              Admin Instructions (Internal)
            </label>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id="tooltip-internal-usage-warning">
                  {
                    'This box is only for internal use by office administrators and the answering service. Showing agents will not see these details.'
                  }
                </Tooltip>
              }
            >
              <i className="fa fa-info-circle m-l-sm font-size-16"></i>
            </OverlayTrigger>
          </div>
          <textarea
            className="form-control"
            placeholder="Showing instructions"
            rows={4}
            data-cy="showing-instructions"
            value={showingSettings.showingInstructions}
            onChange={e => {
              const _showingInstructions = e.target.value;
              setShowingSettings(prev => ({
                ...prev,
                showingInstructions: _showingInstructions,
              }));
            }}
          ></textarea>
          {showingSettings.showVendorInstructions && (
            <textarea
              className="form-control m-t-sm"
              placeholder="Vendor confirmation instructions"
              rows={4}
              data-cy="vendor-confrm-instructions"
              value={showingSettings.vendorInstructions}
              onChange={e => {
                const _vendorInstructions = e.target.value;
                setShowingSettings(prev => ({
                  ...prev,
                  vendorInstructions: _vendorInstructions,
                }));
              }}
            ></textarea>
          )}
          {(!showingSettings.showVendorInstructions ||
            !showingSettings.vendorInstructions) && (
            <a
              className="m-t-sm add-extra-box-config"
              style={{
                color: showingSettings.showVendorInstructions
                  ? '#fc5858'
                  : '#588cfc',
              }}
              onClick={() =>
                setShowingSettings(prev => ({
                  ...prev,
                  showVendorInstructions: !prev.showVendorInstructions,
                }))
              }
              data-cy="add-remove-vendor-instructions"
            >
              {`${
                showingSettings.showVendorInstructions ? '- Remove' : '+ Add'
              } Vendor Instructions`}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default (props: any) => (
  <HashRouter>
    <Route render={() => <ShowingSettings {...props} />} />
  </HashRouter>
);

const StyledContainer = styled.div<{ $xl: any }>`
  display: flex;
  ...(${props => (props.$xl ? { 'justify-content': 'center' } : {})});
  flex-direction: ${props => (props.$xl ? 'row' : 'column')};
`;
